/**
 * Application context: configuration, transport, cart, navigation, session.
 *
 * All of it is deliberately explicit rather than global. The transport is
 * injected so a test can never fall through to the network; the configuration
 * is injected so a test can drive the "checkout switched off" state that the
 * WP04 stop condition requires; the cart is a value, not a store, so the same
 * component tree renders identically on the server and in the browser.
 *
 * The one asymmetry is deliberate: `cart` is restored from tab storage during
 * hydration, never during server rendering. The server has no idea what is in
 * anyone's cart and must not guess, so the first paint shows an empty cart
 * count and the hydrated app corrects it. That is the only visible difference
 * between the two renders, and it is a fact about the customer rather than a
 * fact about the catalogue.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from "react";
import { MAX_CART_ITEMS } from "../lib/commands.js";
import { UNCONFIGURED, type StorefrontConfig } from "../lib/config.js";
import {
  getServerSnapshot as tabServerSnapshot,
  getSnapshot as tabSnapshot,
  readySnapshot as tabReadySnapshot,
  readyServerSnapshot as tabReadyServerSnapshot,
  reset as resetTab,
  subscribe as subscribeTab,
  update as updateTab,
  type TabState
} from "../lib/handoff.js";
import { type Route, parseRoute } from "../lib/routes.js";
import type { PublicItem } from "../lib/publicContract.js";
import type { Transport } from "../lib/transport.js";

export interface CheckoutHandoff {
  readonly quoteID: string | null;
  readonly orderID: string | null;
  readonly checkoutToken: string | null;
}

/**
 * What the server already fetched and rendered into the document, keyed by the
 * core path it came from. A view uses it only when the path matches exactly, so
 * a client-side navigation to a different page can never be served a stale
 * preload, and any reload goes to core.
 */
export interface Preload {
  readonly path: string;
  readonly items?: readonly PublicItem[];
  readonly item?: PublicItem;
}

export interface StorefrontValue {
  readonly config: StorefrontConfig;
  readonly transport: Transport;
  readonly preload: Preload | null;
  readonly route: Route;
  readonly navigate: (href: string) => void;
  readonly cart: readonly string[];
  /** Previously observed public facts, in memory only; never availability authority. */
  readonly observedItems: Readonly<Record<string, PublicItem>>;
  readonly rememberItem: (item: PublicItem) => void;
  readonly addToCart: (itemID: string) => "added" | "already" | "full";
  readonly removeFromCart: (itemID: string) => void;
  readonly clearCart: () => void;
  readonly handoff: CheckoutHandoff;
  /**
   * False for the server render and for the hydration render that matches it,
   * true once `handoff` is the browser's own tab rather than the empty server
   * snapshot. A one-shot request that needs the tab's checkout token must wait
   * for this; see `ConfirmationView`.
   */
  readonly tabReady: boolean;
  readonly rememberCheckout: (next: CheckoutHandoff) => void;
  readonly forgetCheckout: () => void;
  /** In-memory only. Set after core issues a customer session; never persisted. */
  readonly csrfToken: string | null;
  readonly setCsrfToken: (token: string | null) => void;
}

const StorefrontContext = createContext<StorefrontValue | null>(null);

export interface StorefrontProviderProps {
  readonly config: StorefrontConfig;
  readonly transport: Transport;
  readonly initialRoute: Route;
  readonly preload?: Preload | null;
  /** False during server rendering, where there is no tab and no history. */
  readonly interactive: boolean;
  readonly children: ReactNode;
}

export function StorefrontProvider({
  config,
  transport,
  initialRoute,
  preload = null,
  interactive,
  children
}: StorefrontProviderProps): ReactNode {
  const [route, setRoute] = useState<Route>(initialRoute);
  const [bootstrap, setBootstrap] = useState({ route: initialRoute, preload });
  // Retire the document preload on leave, including routes that unmount the
  // resource hook. Returning later must ask core again.
  if (bootstrap.preload !== null && route !== bootstrap.route) {
    setBootstrap({ route, preload: null });
  }
  const eligiblePreload = route === bootstrap.route ? bootstrap.preload : null;
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [observedItems, setObservedItems] = useState<Readonly<Record<string, PublicItem>>>({});
  const rememberItem = useCallback((item: PublicItem) => {
    setObservedItems((current) => current[item.physical_item_id] === item
      ? current : { ...current, [item.physical_item_id]: item });
  }, []);

  /*
   * The tab store is read through `useSyncExternalStore` rather than restored in
   * an effect. The server snapshot is always the empty cart, so the hydration
   * render matches the server markup exactly, and the tab's real cart is adopted
   * in the same commit instead of after a visible flash.
   */
  const tab: TabState = useSyncExternalStore(
    subscribeTab,
    interactive ? tabSnapshot : tabServerSnapshot,
    tabServerSnapshot
  );
  /*
   * Read through the same store as `tab`, so it can only become true in a
   * render that is already serving the browser snapshot.
   */
  const tabReady = useSyncExternalStore(
    subscribeTab,
    interactive ? tabReadySnapshot : tabReadyServerSnapshot,
    tabReadyServerSnapshot
  );
  const cart = tab.cart;
  const handoff: CheckoutHandoff = useMemo(
    () => ({ quoteID: tab.quoteID, orderID: tab.orderID, checkoutToken: tab.checkoutToken }),
    [tab.quoteID, tab.orderID, tab.checkoutToken]
  );

  useEffect(() => {
    if (!interactive) return;
    const onPopState = (): void => {
      setRoute(parseRoute(window.location.pathname, window.location.search));
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [interactive]);

  const persist = useCallback(
    (nextCart: readonly string[], nextHandoff: CheckoutHandoff) => {
      if (!interactive) return;
      updateTab({
        cart: [...nextCart],
        quoteID: nextHandoff.quoteID,
        orderID: nextHandoff.orderID,
        checkoutToken: nextHandoff.checkoutToken
      });
    },
    [interactive]
  );

  const navigate = useCallback(
    (href: string) => {
      if (!interactive) return;
      const url = new URL(href, window.location.origin);
      // Same-origin only. An external destination is a link, never a navigate().
      if (url.origin !== window.location.origin) return;
      window.history.pushState(null, "", `${url.pathname}${url.search}`);
      setRoute(parseRoute(url.pathname, url.search));
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    [interactive]
  );

  const addToCart = useCallback(
    (itemID: string): "added" | "already" | "full" => {
      // One of one: adding the same object twice is a mistake, not a quantity.
      if (cart.includes(itemID)) return "already";
      if (cart.length >= MAX_CART_ITEMS) return "full";
      persist([...cart, itemID], handoff);
      return "added";
    },
    [cart, handoff, persist]
  );

  const removeFromCart = useCallback(
    (itemID: string) => {
      persist(
        cart.filter((entry) => entry !== itemID),
        handoff
      );
    },
    [cart, handoff, persist]
  );

  const clearCart = useCallback(() => {
    persist([], handoff);
  }, [handoff, persist]);

  const rememberCheckout = useCallback(
    (next: CheckoutHandoff) => {
      persist(cart, next);
    },
    [cart, persist]
  );

  const forgetCheckout = useCallback(() => {
    if (interactive) resetTab();
  }, [interactive]);

  const value = useMemo<StorefrontValue>(
    () => ({
      config,
      transport,
      preload: eligiblePreload,
      route,
      navigate,
      cart,
      observedItems,
      rememberItem,
      addToCart,
      removeFromCart,
      clearCart,
      handoff,
      tabReady,
      rememberCheckout,
      forgetCheckout,
      csrfToken,
      setCsrfToken
    }),
    [
      config,
      transport,
      eligiblePreload,
      route,
      navigate,
      cart,
      observedItems,
      rememberItem,
      addToCart,
      removeFromCart,
      clearCart,
      handoff,
      tabReady,
      rememberCheckout,
      forgetCheckout,
      csrfToken
    ]
  );

  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
}

export function useStorefront(): StorefrontValue {
  const value = useContext(StorefrontContext);
  if (value === null) {
    throw new Error("useStorefront was called outside a StorefrontProvider");
  }
  return value;
}

/** For tests and the server render, which both build a context by hand. */
export const UNCONFIGURED_CONFIG = UNCONFIGURED;
